import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Loader2, Home, X, SlidersHorizontal } from "lucide-react";
import toast from "react-hot-toast";

import {
  getCmaReport, getCmaRecords, publishCmaReport, setCmaComparables, updateCmaReport,
} from "../api/prospectingReportsApi";
import { cleanCriteria, defaultCriteria, getCmaReportType } from "../cmaReportTypes";
import CmaResultsSummary from "../components/CmaResultsSummary";
import CmaRecordSelection from "../components/CmaRecordSelection";
import CmaReportSetup from "../components/CmaReportSetup";
import CmaSearchOptions from "../components/CmaSearchOptions";
import PriceStatementForm from "../components/PriceStatementForm";
import ReportReadyModal from "../components/ReportReadyModal";

const NAVY = "#1f2a44";

// The steps after "Start CMA Report", as on Property Data.
const CMA_STEPS = [
  { key: "summary", title: "Results Summary" },
  { key: "records", title: "Select Records" },
  { key: "setup", title: "Report Setup" },
];
const STATEMENT_STEPS = [
  { key: "summary", title: "Results Summary" },
  { key: "records", title: "Select Comparables" },
  { key: "statement", title: "Statement Details" },
];

const n = (v) => (v != null && v !== "" ? Number(v) : null);

// Strip the server-only fields off a record before saving it, so a row from
// the search and a row the agent typed look the same.
const cleanComparable = (row) => ({
  kind: row.kind || "sold",
  group: row.group || "suburb",
  full_address: row.full_address || "",
  suburb: row.suburb || "",
  state: row.state || "",
  postcode: row.postcode || "",
  latitude: row.latitude ?? null,
  longitude: row.longitude ?? null,
  property_type: row.property_type || "",
  bedrooms: row.bedrooms ?? null,
  bathrooms: row.bathrooms ?? null,
  car_spaces: row.car_spaces ?? null,
  land_size: row.land_size ?? null,
  price: row.price ?? null,
  price_label: row.price_label || "",
  sold_date: row.sold_date || null,
  listed_date: row.listed_date || null,
  days_on_market: row.days_on_market ?? null,
  distance_km: row.distance_km ?? null,
  image_url: row.image_url || "",
  source: row.source || "manual",
  source_id: String(row.source_id || ""),
});

/**
 * The report builder after the target address and Search Options:
 *
 *   Sales / Rental CMA:        Results Summary > Select Records > Report Setup
 *   Property Price Statement:  Results Summary > Select Comparables > Statement Details
 *
 * State is saved to the API on each step change, not on every keystroke.
 */
const CmaWizardPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [records, setRecords] = useState(null);
  const [recordsLoading, setRecordsLoading] = useState(true);
  const [recordsError, setRecordsError] = useState(false);
  // The Select Records tab a Results Summary count was clicked for.
  const [openTab, setOpenTab] = useState(null);
  const [showReady, setShowReady] = useState(false);
  const [showRefine, setShowRefine] = useState(false);
  const [refineCriteria, setRefineCriteria] = useState({});
  const [publishErrors, setPublishErrors] = useState({});

  const [comparables, setComparables] = useState([]);
  const [insights, setInsights] = useState(null);
  const [setup, setSetup] = useState({});
  const [appraisal, setAppraisal] = useState({});
  const [pps, setPps] = useState({});
  const pretickedRef = useRef(false);

  const type = getCmaReportType(report?.report_type);
  const steps = type.statement ? STATEMENT_STEPS : CMA_STEPS;
  const stepKey = steps.some((s) => s.key === searchParams.get("step")) ? searchParams.get("step") : "summary";
  const stepIndex = steps.findIndex((s) => s.key === stepKey);
  const listUrl = report?.report_type && report.report_type !== "sales"
    ? `/prospecting/cma?type=${report.report_type}`
    : "/prospecting/cma";

  const loadRecords = useCallback(async () => {
    setRecordsLoading(true);
    setRecordsError(false);
    try {
      const { data } = await getCmaRecords(id);
      setRecords(data);
      return data;
    } catch (error) {
      // Shown as an error with a retry, never as "0 results" (Monday 13066303151).
      setRecordsError(true);
      setRecords({ groups: {}, summary: {}, total: 0 });
      return null;
    } finally {
      setRecordsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    getCmaReport(id)
      .then(({ data }) => {
        if (cancelled) return;
        setReport(data);
        setComparables(data.comparables || []);
        setInsights(data.insights?.headline ? data.insights : null);
        setSetup({
          template: data.template || "modern",
          theme_colour: data.theme_colour || NAVY,
          image_url: data.image_url || "",
          agent_blurb: data.agent_blurb || "",
          prepared_by: data.prepared_by || "",
          recipient_name: data.recipient_name || "",
          sections: data.sections || {},
        });
        setAppraisal({
          sale_estimate_mode: data.sale_estimate_mode || "none",
          sale_estimate_min: n(data.sale_estimate_min),
          sale_estimate_max: n(data.sale_estimate_max),
          rental_estimate_mode: data.rental_estimate_mode || "none",
          rental_estimate_min: n(data.rental_estimate_min),
          rental_estimate_max: n(data.rental_estimate_max),
          appraisal_comment: data.appraisal_comment || "",
        });
        setPps({
          statement: {
            location: data.search_criteria?.location || "metro",
            price_type: "range",
            ...(data.statement || {}),
          },
          property_type: data.property_type || "",
          bedrooms: data.bedrooms,
          bathrooms: data.bathrooms,
          car_spaces: data.car_spaces,
          land_size: data.land_size,
        });
      })
      .catch(() => {
        toast.error("Could not open that report.");
        navigate("/prospecting/cma");
      })
      .finally(() => !cancelled && setLoading(false));
    loadRecords();
    return () => { cancelled = true; };
  }, [id, navigate, loadRecords]);

  // Property Data ticks every record on a CMA to start with. A statement
  // starts with none, because the agent must pick the three most comparable.
  useEffect(() => {
    if (pretickedRef.current || !report || !records || type.statement) return;
    pretickedRef.current = true;
    if ((report.comparables || []).length > 0) return;
    const all = Object.values(records.groups || {}).flat();
    setComparables(all);
  }, [report, records, type.statement]);

  const persist = useCallback(async () => {
    setSaving(true);
    try {
      await setCmaComparables(id, comparables.map(cleanComparable));
      const body = type.statement
        ? {
          ...pps,
          sale_estimate_min: appraisal.sale_estimate_min,
          sale_estimate_max: appraisal.sale_estimate_max,
          sale_estimate_mode: "manual",
        }
        : { ...appraisal, ...setup, insights: insights || {} };
      const { data } = await updateCmaReport(id, body);
      setReport((prev) => ({ ...prev, ...data }));
      return data;
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not save your changes.");
      throw error;
    } finally {
      setSaving(false);
    }
  }, [id, comparables, appraisal, insights, setup, pps, type.statement]);

  const goTo = async (index, tab = null) => {
    if (index > stepIndex) {
      try { await persist(); } catch (error) { return; }
    }
    setOpenTab(tab);
    setSearchParams({ step: steps[index].key }, { replace: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const finish = async () => {
    try { await persist(); } catch (error) { return; }
    if (!type.statement) {
      setShowReady(true);
      return;
    }
    try {
      const { data } = await publishCmaReport(id);
      setPublishErrors({});
      setReport((prev) => ({ ...prev, ...data }));
      setShowReady(true);
    } catch (error) {
      const errors = error?.response?.data?.errors || {};
      setPublishErrors(errors);
      toast.error(Object.values(errors)[0] || error?.response?.data?.detail || "Could not publish the statement.");
    }
  };

  const openRefine = () => {
    setRefineCriteria({ ...defaultCriteria(report?.report_type), ...(report?.search_criteria || {}) });
    setShowRefine(true);
  };

  const applyRefine = async () => {
    try {
      const { data } = await updateCmaReport(id, { search_criteria: cleanCriteria(refineCriteria) });
      setReport((prev) => ({ ...prev, ...data }));
      setShowRefine(false);
      await loadRecords();
    } catch (error) {
      toast.error("Could not update the search.");
    }
  };

  // The statement step edits the subject's own fields too, so merge them in.
  const statementForm = useMemo(() => ({
    ...pps,
    sale_estimate_min: appraisal.sale_estimate_min,
    sale_estimate_max: appraisal.sale_estimate_max,
  }), [pps, appraisal]);

  const onStatementChange = (patch) => {
    // A publish error describes the form as it was; once the agent edits it,
    // the live checks take over until they publish again.
    if (Object.keys(publishErrors).length) setPublishErrors({});
    const { sale_estimate_min: lo, sale_estimate_max: hi, ...rest } = patch;
    if ("sale_estimate_min" in patch || "sale_estimate_max" in patch) {
      setAppraisal((prev) => ({
        ...prev,
        ...("sale_estimate_min" in patch ? { sale_estimate_min: lo } : {}),
        ...("sale_estimate_max" in patch ? { sale_estimate_max: hi } : {}),
      }));
    }
    if (Object.keys(rest).length) setPps((prev) => ({ ...prev, ...rest }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-gray-400">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  const isLast = stepIndex === steps.length - 1;
  const finishLabel = type.statement
    ? (report?.published_at ? "Save Statement" : "Publish Statement")
    : "Create Report";

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wide mb-0.5" style={{ color: NAVY }}>
            {type.label}
          </p>
          <h1 className="text-xl font-bold text-gray-900">{steps[stepIndex].title}</h1>
          <p className="text-xs text-gray-500 mt-0.5">Step {stepIndex + 1} of {steps.length}</p>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-100 min-w-0">
            <div className="w-9 h-9 rounded bg-gray-200 overflow-hidden flex items-center justify-center flex-shrink-0">
              {report?.image_url
                ? <img src={report.image_url} alt="" className="w-full h-full object-cover" />
                : <Home className="w-4 h-4 text-gray-400" />}
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-gray-900 truncate max-w-[220px]">{report?.full_address}</p>
              <p className="text-[11px] text-gray-500">
                {[
                  report?.bedrooms != null && `${report.bedrooms} bed`,
                  report?.bathrooms != null && `${report.bathrooms} bath`,
                  report?.land_size && `${Math.round(report.land_size)}m²`,
                ].filter(Boolean).join(" · ")}
              </p>
            </div>
          </div>
          <button type="button" onClick={() => navigate(listUrl)} className="p-2 rounded-lg hover:bg-gray-50 text-gray-400"
            aria-label="Close the report">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Property Data's toolbar: Refine Search / Start Again on the left. */}
      <div className="flex flex-wrap items-center gap-2 mb-4 p-2 rounded-lg border border-gray-100 bg-white">
        <button type="button" onClick={openRefine}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:text-gray-900">
          <SlidersHorizontal className="w-4 h-4" /> Refine Search
        </button>
        <button type="button" onClick={() => navigate(listUrl)}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900">
          <ChevronLeft className="w-4 h-4" /> Start Again
        </button>
        <div className="flex gap-1 ml-auto" aria-label="Progress">
          {steps.map((s, i) => (
            <button
              key={s.key}
              type="button"
              onClick={() => goTo(i)}
              className="h-1.5 w-10 sm:w-16 rounded-full"
              style={{ backgroundColor: i <= stepIndex ? NAVY : "#e5e7eb" }}
              aria-label={`Go to step ${i + 1}: ${s.title}`}
            />
          ))}
        </div>
      </div>

      <div className="min-h-[300px]">
        {stepKey === "summary" && (
          <CmaResultsSummary records={records} loading={recordsLoading} error={recordsError}
            rental={type.rental} statement={type.statement} onRefine={openRefine} onRetry={loadRecords}
            onOpenGroup={(key) => goTo(steps.findIndex((s) => s.key === "records"), key)} />
        )}
        {stepKey === "records" && (
          recordsLoading ? (
            <div className="flex items-center justify-center py-20 text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : (
            <CmaRecordSelection report={report} records={records} selected={comparables} onChange={setComparables}
              rental={type.rental} statement={type.statement} initialTab={openTab} />
          )
        )}
        {stepKey === "setup" && (
          <CmaReportSetup
            report={report}
            setup={setup}
            onChange={(patch) => setSetup((prev) => ({ ...prev, ...patch }))}
            insights={insights}
            onInsightsChange={setInsights}
            appraisal={appraisal}
            onAppraisalChange={setAppraisal}
            comparables={comparables}
            rental={type.rental}
          />
        )}
        {stepKey === "statement" && (
          <PriceStatementForm report={report} form={statementForm} onChange={onStatementChange}
            comparablesCount={comparables.length} errors={publishErrors} />
        )}
      </div>

      <div className="flex items-center justify-between gap-3 mt-8 pt-4 border-t border-gray-100">
        <button
          type="button"
          onClick={() => (stepIndex === 0 ? navigate(listUrl) : goTo(stepIndex - 1))}
          className="inline-flex items-center gap-1 px-4 py-2.5 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50"
        >
          <ChevronLeft className="w-4 h-4" />
          {stepIndex === 0 ? "Exit" : steps[stepIndex - 1].title}
        </button>

        <p className="text-xs text-gray-500 hidden sm:block">
          {comparables.length} record{comparables.length === 1 ? "" : "s"} selected
        </p>

        <button
          type="button"
          onClick={() => (isLast ? finish() : goTo(stepIndex + 1))}
          disabled={saving || (stepKey === "summary" && recordsLoading)}
          className="inline-flex items-center gap-1 px-5 py-2.5 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
          style={{ backgroundColor: NAVY }}
        >
          {saving && <Loader2 className="w-4 h-4 animate-spin" />}
          {isLast ? finishLabel : stepKey === "summary" ? "Continue" : stepKey === "records" && !type.statement ? "Proceed to Report Setup" : "Continue"}
          {!isLast && <ChevronRight className="w-4 h-4" />}
        </button>
      </div>

      {showRefine && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-xl p-4 sm:p-5 text-white"
            style={{ backgroundColor: type.rental ? "#0a9fd8" : NAVY }} role="dialog" aria-label="Refine Search">
            <div className="flex justify-end -mt-1 mb-1">
              <button type="button" onClick={() => setShowRefine(false)} aria-label="Close"
                className="p-1 rounded hover:bg-white/10"><X className="w-5 h-5" /></button>
            </div>
            <CmaSearchOptions reportType={type.key} value={refineCriteria} onChange={setRefineCriteria} />
            <div className="flex justify-end mt-5">
              <button type="button" onClick={applyRefine}
                className="px-6 py-2.5 rounded-md bg-white text-base text-gray-900 hover:bg-gray-50">
                Search again
              </button>
            </div>
          </div>
        </div>
      )}

      {showReady && (
        <ReportReadyModal report={report} onClose={() => setShowReady(false)} onDone={() => navigate(listUrl)} />
      )}
    </div>
  );
};

export default CmaWizardPage;
